import { getNumeralsPref } from "@/lib/prefs";

export function isRtl(): boolean {
  if (typeof document === "undefined") return true;
  return document.documentElement.dir === "rtl";
}

export function documentDir(): "rtl" | "ltr" {
  return isRtl() ? "rtl" : "ltr";
}

export function localeTag(): string {
  if (typeof document === "undefined") return "ar-EG";
  const lang = document.documentElement.lang || "ar";
  return lang.startsWith("ar") ? "ar-EG" : "en-US";
}

/**
 * Locale tag used specifically for number formatting. Honours the user's
 * `numerals` preference in the Arabic view: `"ar"` forces Arabic-Indic digits,
 * `"en"` forces Western digits (`ar-EG-u-nu-latn` keeps grouping/decimals
 * Arabic-localized but with Latin numerals), and `"auto"` keeps the default
 * Arabic-Indic digits. The English view always uses Latin digits. Date/time
 * formatting deliberately keeps using `localeTag()` so months/AM-PM stay
 * localized. [task #2]
 */
export function numberLocaleTag(): string {
  if (typeof document === "undefined") return "ar-EG";
  const lang = document.documentElement.lang || "ar";
  if (!lang.startsWith("ar")) return "en-US";
  switch (getNumeralsPref()) {
    case "en":
      return "ar-EG-u-nu-latn";
    case "ar":
      return "ar-EG";
    default:
      return "ar-EG";
  }
}

export function formatNumber(value: number): string {
  return value.toLocaleString("en-US");
}

/**
 * Locale tag for dates whose month names follow the UI language while digits
 * stay Western (Latin) regardless of the numerals preference: Arabic UI gets
 * e.g. `19 يونيو 2026`, English UI gets `19 Jun 2026`.
 */
export function latinDigitsLocaleTag(): string {
  if (typeof document === "undefined") return "ar-EG-u-nu-latn";
  const lang = document.documentElement.lang || "ar";
  return lang.startsWith("ar") ? "ar-EG-u-nu-latn" : "en-US";
}

export function toEnglishDigits(value: string | number): string {
  return String(value).replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x660));
}

/**
 * Locale-aware date/time formatters. Arabic UI gets Arabic month names with
 * Latin digits (via `latinDigitsLocaleTag()`) instead of the Latin `en-CA`
 * strings that previously showed even to Arabic users. Arabic-month dates must
 * follow the page direction (RTL) so they read day-first — do NOT wrap them in
 * `dir="ltr"`, which flips them to read year-first. Only fully-Latin dates
 * (e.g. `en-US`) need an LTR run. [audit P2-4]
 */
export function formatDate(value: string | number | Date, locale = localeTag()): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

export function formatDateTime(value: string | number | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(localeTag(), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
